/**
 * Shared MCQ Hydration Function
 * Handles DOM manipulation for MCQ rendering in both Reading and Listening tests
 */

import {
  parseMCQContainer,
  expandQuestionRange,
  getIeltsMultipleAnswerMatchResults,
} from './mcqHelpers';

/**
 * Extract question numbers from MCQ container
 * @param {HTMLElement} mcqContainer - The .mcq-container DOM element
 * @param {string} mcqType - 'single' or 'multiple'
 * @param {string} questionRange - Range string like "17-18" or empty for single
 * @returns {string[]} Array of question numbers
 */
function extractQuestionNumbersFromMCQ(mcqContainer, mcqType, questionRange) {
  let questionNumbers = [];
  
  if (mcqType === 'single') {
    // For single, first try to extract from attributes / question text (common: "1." or "1)")
    const attrQNum = mcqContainer.getAttribute('data-question-number');
    let qNum = (attrQNum && /^\d+$/.test(attrQNum)) ? attrQNum : null;

    if (!qNum) {
      const qt =
        mcqContainer.querySelector('.mcq-question-text') ||
        mcqContainer.querySelector('.mcq-question-text-rendered');
      const qtText = qt?.textContent?.trim() || '';
      const qtMatch = qtText.match(/^(\d+)\s*[\.\)]\s*/);
      if (qtMatch && qtMatch[1]) qNum = qtMatch[1];
    }

    // Fallback: try to find question number from preceding content
    let prevSibling = mcqContainer.previousElementSibling;
    while (prevSibling && !qNum) {
      if (prevSibling.tagName === 'P') {
        const numberMatch =
          prevSibling.innerHTML.match(/(&lt;(\d+)&gt;|<(\d+)>)/) ||
          prevSibling.textContent?.match(/^\s*(\d+)\s*[\.\)]\s*/);
        if (numberMatch) {
          qNum = numberMatch[2] || numberMatch[3] || numberMatch[1];
          break;
        }
      }
      prevSibling = prevSibling.previousElementSibling;
    }
    if (qNum && /^\d+$/.test(qNum)) {
      questionNumbers = [qNum];
    }
  } else if (mcqType === 'multiple' && questionRange) {
    questionNumbers = expandQuestionRange(questionRange);
  }
  
  return questionNumbers;
}

/**
 * Update existing rendered MCQ (checkboxes/radios) based on current answers
 * @param {HTMLElement} existingRendered - The .mcq-container-rendered element
 * @param {HTMLElement} mcqContainer - The original .mcq-container element
 * @param {string} mcqType - 'single' or 'multiple'
 * @param {string[]} questionNumbers - Array of question numbers
 * @param {Object} allAnswers - Current user answers object
 */
function updateExistingMCQRendered(existingRendered, mcqContainer, mcqType, questionNumbers, allAnswers) {
  if (mcqType === 'multiple') {
    const checkboxes = existingRendered.querySelectorAll('input[type="checkbox"]');
    const selectedOptionsMap = {};
    questionNumbers.forEach((qNum) => {
      const userAnswer = allAnswers[qNum] || '';
      if (userAnswer) {
        selectedOptionsMap[userAnswer] = qNum;
      }
    });
    checkboxes.forEach((checkbox) => {
      const isSelected = selectedOptionsMap[checkbox.value] !== undefined;
      checkbox.checked = isSelected;
    });
    // Update the info div
    const infoDiv = existingRendered.querySelector('.mcq-info-text');
    if (infoDiv && questionNumbers.length > 0) {
      const totalSelected = Object.keys(selectedOptionsMap).length;
      const requiredCount = mcqContainer.getAttribute('data-required-count');
      const questionRange = mcqContainer.getAttribute('data-question-range') || '';
      // infoDiv.textContent = `${questionRange ? `Questions ${questionRange}: ` : ''}Select ${requiredCount} answer${parseInt(requiredCount) > 1 ? 's' : ''}. ${totalSelected > 0 ? `Selected: ${totalSelected} of ${requiredCount}` : ''}`;
    }
  } else {
    // Single answer - update radios
    const qNum = questionNumbers[0];
    if (qNum) {
      const userAnswer = allAnswers[qNum] || '';
      const radios = existingRendered.querySelectorAll(`input[type="radio"][name="mcq-${qNum}"]`);
      radios.forEach((radio) => {
        radio.checked = radio.value === userAnswer;
      });
    }
  }
}

/**
 * Create MCQ wrapper element with question text
 * @param {string} questionText - The question text
 * @returns {HTMLElement} The wrapper div element
 */
function createMCQWrapper(questionText) {
  const mcqWrapper = document.createElement('div');
  mcqWrapper.className = 'mcq-container-rendered';
  mcqWrapper.style.marginBottom = '20px';
  
  // Question text
  if (questionText) {
    const questionTextDiv = document.createElement('div');
    questionTextDiv.className = 'mcq-question-text-rendered';
    questionTextDiv.style.fontWeight = '400';
    questionTextDiv.style.fontSize = '1rem';
    questionTextDiv.style.marginBottom = '12px';
    questionTextDiv.style.color = '#333';
    questionTextDiv.innerHTML = questionText;
    mcqWrapper.appendChild(questionTextDiv);
  }
  
  return mcqWrapper;
}

/**
 * Create single-answer MCQ options (radio buttons)
 * @param {Array} options - Array of {value, text} objects
 * @param {string} qNum - Question number
 * @param {string} userAnswer - Current user answer
 * @param {string} correctAnswer - Correct answer
 * @param {boolean} isCorrect - Whether user answer is correct
 * @param {Object} apiResult - API result object (if in review mode)
 * @param {Function} onAnswerChange - Callback: (qNum, value) => void
 * @returns {HTMLElement} Options container div
 */
function createSingleAnswerOptions(options, qNum, userAnswer, correctAnswer, isCorrect, apiResult, onAnswerChange) {
  const optionsContainer = document.createElement('div');
  optionsContainer.className = 'mcq-options-rendered';
  
  options.forEach((opt, optIdx) => {
    const label = document.createElement('label');
    label.style.display = 'flex';
    label.style.alignItems = 'center';
    label.style.padding = '8px 0';
    label.style.marginBottom = '8px';
    label.style.cursor = apiResult ? 'default' : 'pointer';
    label.style.transition = 'all 0.2s';
    label.style.gap = '0';
    
    const isSelected = userAnswer === opt.value;
    const isOptCorrect = correctAnswer === opt.value;
    
    const optionLetter = document.createElement('span');
    optionLetter.style.fontWeight = '600';
    optionLetter.style.marginRight = '8px';
    optionLetter.style.flexShrink = '0';
    optionLetter.style.width = '26px';
    optionLetter.style.height = '26px';
    optionLetter.style.borderRadius = '50%';
    optionLetter.style.backgroundColor = '#E0E0E0';
    optionLetter.style.display = 'flex';
    optionLetter.style.alignItems = 'center';
    optionLetter.style.justifyContent = 'center';
    optionLetter.style.fontSize = '14px';
    optionLetter.style.color = '#333';
    optionLetter.textContent = opt.value;
    
    if (apiResult) {
      // Review mode: green for correct, red for wrong
      if (isOptCorrect) {
        label.style.color = '#37965E';
        label.style.fontWeight = '500';
        label.style.backgroundColor = '#E8F5E9';
        label.style.borderRadius = '6px';
        label.style.padding = '10px 12px';
        label.style.marginBottom = '8px';
        label.style.marginLeft = '-12px';
      } else if (isSelected && !isOptCorrect) {
        label.style.color = '#E53935';
        label.style.fontWeight = '500';
        label.style.backgroundColor = '#FFF3F3';
        label.style.borderRadius = '6px';
        label.style.padding = '10px 12px';
        label.style.marginBottom = '8px';
        label.style.marginLeft = '-12px';
      }
    }
    
    const radio = document.createElement('input');
    radio.type = 'radio';
    radio.name = `mcq-${qNum}`;
    radio.value = opt.value;
    radio.checked = isSelected;
    radio.disabled = !!apiResult;
    radio.style.marginRight = '8px';
    radio.style.cursor = apiResult ? 'default' : 'pointer';
    radio.style.width = '18px';
    radio.style.height = '18px';
    radio.style.flexShrink = '0';
    radio.style.accentColor = apiResult 
      ? (isOptCorrect ? '#37965E' : isSelected && !isOptCorrect ? '#E53935' : '#996CFE')
      : '#996CFE';
    
    if (!apiResult && onAnswerChange) {
      radio.onchange = (e) => {
        onAnswerChange(qNum, e.target.value);
      };
    }
    
    const optionText = document.createElement('span');
    optionText.style.flex = '1';
    optionText.style.lineHeight = '1.5';
    optionText.style.wordBreak = 'break-word';
    optionText.style.overflowWrap = 'break-word';
    optionText.textContent = opt.text;
    
    label.appendChild(optionLetter);
    label.appendChild(radio);
    label.appendChild(optionText);
    
    optionsContainer.appendChild(label);
  });
  
  return optionsContainer;
}

/**
 * Create multiple-answer MCQ options (checkboxes)
 * @param {Array} options - Array of {value, text} objects
 * @param {string[]} questionNumbers - Array of question numbers
 * @param {string[]} correctAnswers - Array of correct answers
 * @param {string} questionRange - Range string like "17-18"
 * @param {number} requiredCount - Required number of answers
 * @param {Object} allAnswers - Current user answers object
 * @param {Object} apiResult - API result object (if in review mode)
 * @param {Function} onAnswerChange - Callback: (qNum, value) => void
 * @returns {HTMLElement} Options container div
 */
function createMultipleAnswerOptions(options, questionNumbers, correctAnswers, questionRange, requiredCount, allAnswers, apiResult, onAnswerChange) {
  const optionsContainer = document.createElement('div');
  optionsContainer.className = 'mcq-options-rendered';
  
  const expectedAnswers = Array.isArray(correctAnswers) ? correctAnswers : [correctAnswers];
  const reviewAnswers = questionNumbers.map((qNum) => ({
    qNum,
    userAnswer: apiResult?.response?.analytics?.[qNum]?.userAnswer || allAnswers[qNum] || '',
    isCorrect: apiResult?.response?.analytics?.[qNum]?.isCorrect === true,
  }));
  const getReviewAnswerValues = (answer) => {
    if (Array.isArray(answer)) return answer;
    if (typeof answer === 'string' && answer.trim().startsWith('[') && answer.trim().endsWith(']')) {
      try {
        const parsed = JSON.parse(answer);
        if (Array.isArray(parsed)) return parsed;
      } catch (_) {
        // Treat malformed legacy values as a scalar answer.
      }
    }
    return answer ? [answer] : [];
  };
  const optionMatchesAnswer = (answer, optionValue) => (
    getIeltsMultipleAnswerMatchResults([answer], [optionValue], options, 1)[0] === true
  );
  const selectedOptionsMap = {};

  if (apiResult) {
    options.forEach((option) => {
      const selectedAnswer = reviewAnswers.find(({ userAnswer }) => (
        getReviewAnswerValues(userAnswer).some(answer => optionMatchesAnswer(answer, option.value))
      ));
      if (selectedAnswer) selectedOptionsMap[option.value] = selectedAnswer.qNum;
    });
  } else {
    reviewAnswers.forEach(({ qNum, userAnswer }) => {
      if (userAnswer) selectedOptionsMap[userAnswer] = qNum;
    });
  }
  
  const totalSelected = Object.keys(selectedOptionsMap).length;
  const canSelectMore = !requiredCount || totalSelected < parseInt(requiredCount);
  
  options.forEach((opt, optIdx) => {
    const isSelected = selectedOptionsMap[opt.value] !== undefined;
    const mappedQNum = selectedOptionsMap[opt.value];
    const isExpectedOption = expectedAnswers.some(answer => optionMatchesAnswer(answer, opt.value));
    const hasCorrectReviewAnswer = reviewAnswers.some(({ userAnswer, isCorrect }) => (
      isCorrect && getReviewAnswerValues(userAnswer).some(answer => optionMatchesAnswer(answer, opt.value))
    ));
    const isOptCorrect = apiResult
      ? hasCorrectReviewAnswer
      : isExpectedOption;
    const isOptMissing = apiResult && !isSelected && isExpectedOption && !hasCorrectReviewAnswer;
    const isOptWrong = apiResult
      ? isSelected && !hasCorrectReviewAnswer
      : isSelected && !isExpectedOption;
    
    const label = document.createElement('label');
    label.style.display = 'flex';
    label.style.alignItems = 'center';
    label.style.padding = '8px 0';
    label.style.marginBottom = '8px';
    label.style.cursor = (apiResult || (!canSelectMore && !isSelected)) ? 'default' : 'pointer';
    label.style.transition = 'all 0.2s';
    label.style.opacity = (!canSelectMore && !isSelected) ? 0.5 : 1;
    label.style.gap = '0';
    
    const optionLetter = document.createElement('span');
    optionLetter.style.fontWeight = '600';
    optionLetter.style.marginRight = '8px';
    optionLetter.style.flexShrink = '0';
    optionLetter.style.width = '26px';
    optionLetter.style.height = '26px';
    optionLetter.style.borderRadius = '50%';
    optionLetter.style.backgroundColor = '#E0E0E0';
    optionLetter.style.display = 'flex';
    optionLetter.style.alignItems = 'center';
    optionLetter.style.justifyContent = 'center';
    optionLetter.style.fontSize = '14px';
    optionLetter.style.color = '#333';
    optionLetter.textContent = opt.value;
    
    if (apiResult) {
      // Review mode: green for correct, red for wrong
      if (isOptCorrect) {
        label.style.color = '#37965E';
        label.style.fontWeight = '500';
        label.style.backgroundColor = '#E8F5E9';
        label.style.borderRadius = '6px';
        label.style.padding = '10px 12px';
        label.style.marginBottom = '8px';
        label.style.marginLeft = '-12px';
      } else if (isOptWrong) {
        label.style.color = '#E53935';
        label.style.fontWeight = '500';
        label.style.backgroundColor = '#FFF3F3';
        label.style.borderRadius = '6px';
        label.style.padding = '10px 12px';
        label.style.marginBottom = '8px';
        label.style.marginLeft = '-12px';
      } else if (isOptMissing) {
        label.style.color = '#9A6700';
        label.style.fontWeight = '500';
        label.style.backgroundColor = '#FFF8E1';
        label.style.borderRadius = '6px';
        label.style.padding = '10px 12px';
        label.style.marginBottom = '8px';
        label.style.marginLeft = '-12px';
      }
    }
    
    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.value = opt.value;
    checkbox.checked = isSelected;
    checkbox.disabled = !!apiResult || (!canSelectMore && !isSelected);
    checkbox.style.marginRight = '8px';
    checkbox.style.cursor = (apiResult || (!canSelectMore && !isSelected)) ? 'default' : 'pointer';
    checkbox.style.width = '18px';
    checkbox.style.height = '18px';
    checkbox.style.flexShrink = '0';
    checkbox.style.accentColor = apiResult
      ? (isOptCorrect ? '#37965E' : isOptWrong ? '#E53935' : isOptMissing ? '#D69E2E' : '#996CFE')
      : '#996CFE';
    
    if (!apiResult && onAnswerChange) {
      checkbox.onchange = (e) => {
        const selectedOptions = [];
        const allCheckboxes = optionsContainer.querySelectorAll('input[type="checkbox"]');
        allCheckboxes.forEach((cb) => {
          if (cb !== checkbox && cb.checked) {
            selectedOptions.push(cb.value);
          }
        });
        if (e.target.checked) {
          selectedOptions.push(opt.value);
        }
        
        // Map to question numbers
        questionNumbers.forEach((qNum, idx) => {
          if (idx < selectedOptions.length) {
            onAnswerChange(qNum, selectedOptions[idx]);
          } else {
            onAnswerChange(qNum, '');
          }
        });
      };
    }
    
    const optionText = document.createElement('span');
    optionText.style.flex = '1';
    optionText.style.lineHeight = '1.5';
    optionText.style.wordBreak = 'break-word';
    optionText.style.overflowWrap = 'break-word';
    optionText.textContent = apiResult ? opt.text : `${opt.text}${mappedQNum ? ` (Q${mappedQNum})` : ''}`;
    
    label.appendChild(optionLetter);
    label.appendChild(checkbox);
    label.appendChild(optionText);
    
    optionsContainer.appendChild(label);
  });
  
  // Info about required count (simplified, no box)
  if (requiredCount) {
    const infoDiv = document.createElement('div');
    infoDiv.className = 'mcq-info-text';
    infoDiv.style.marginTop = '12px';
    infoDiv.style.fontSize = '0.875rem';
    infoDiv.style.color = '#666';
    // infoDiv.textContent = `${questionRange ? `Questions ${questionRange}: ` : ''}Select ${requiredCount} answer${requiredCount > 1 ? 's' : ''}. ${totalSelected > 0 ? `Selected: ${totalSelected} of ${requiredCount}` : ''}`;
    optionsContainer.appendChild(infoDiv);
  }
  
  return optionsContainer;
}

/**
 * Hydrate MCQ containers in the DOM
 * @param {HTMLElement} containerElement - The container element (usually .ielts-passage-questions)
 * @param {Object} params - Parameters object
 * @param {Object} params.allAnswers - Current user answers object
 * @param {Object} params.apiResult - API result object (if in review mode)
 * @param {Function} params.onAnswerChange - Callback: (qNum, value) => void
 */
export function hydrateMCQContainers(containerElement, { allAnswers = {}, apiResult = null, onAnswerChange = null }) {
  if (!containerElement) return;
  
  const mcqContainers = containerElement.querySelectorAll('.mcq-container');
  mcqContainers.forEach((mcqContainer) => {
    // Check if already rendered (has mcq-container-rendered child)
    const existingRendered = mcqContainer.querySelector('.mcq-container-rendered');
    
    // If already rendered and NOT in review mode, just update the checkboxes/radios
    if (existingRendered && !apiResult) {
      const questionRange = mcqContainer.getAttribute('data-question-range') || '';
      const mcqType = mcqContainer.getAttribute('data-mcq-type') || 'single';
      const questionNumbers = extractQuestionNumbersFromMCQ(mcqContainer, mcqType, questionRange);
      
      if (questionNumbers.length > 0) {
        updateExistingMCQRendered(existingRendered, mcqContainer, mcqType, questionNumbers, allAnswers);
      }
      return; // Skip re-rendering
    }
    
    // If apiResult is present (review mode), always re-render to apply review mode styling
    // When already rendered, we need to clear first, but we can't parse after clearing
    // So we need to extract data from data attributes on the container itself
    let mcqData = null;
    let questionNumbers = [];
    if (existingRendered && apiResult) {
      // Extract data from data attributes (available even after clearing)
      const mcqType = mcqContainer.getAttribute('data-mcq-type') || 'single';
      const questionRange = mcqContainer.getAttribute('data-question-range') || '';

      // IMPORTANT: resolve question numbers BEFORE clearing (single MCQ can disappear otherwise)
      questionNumbers = extractQuestionNumbersFromMCQ(mcqContainer, mcqType, questionRange);
      if (questionNumbers.length === 0) {
        // Can't safely re-render (we'd lose the already-rendered DOM)
        return;
      }

      const requiredCountAttr = mcqContainer.getAttribute('data-required-count');
      const correctAnswersAttr = mcqContainer.getAttribute('data-correct-answers') || '[]';
      
      let correctAnswers = [];
      try {
        correctAnswers = JSON.parse(correctAnswersAttr);
      } catch (e) {
        console.error('Error parsing correct answers:', e);
      }
      
      // Extract question text and options from existing rendered content before clearing
      const questionTextElement = existingRendered.querySelector('.mcq-question-text-rendered');
      const questionText = questionTextElement?.textContent?.trim() || '';
      
      const optionLabels = existingRendered.querySelectorAll('label');
      const options = Array.from(optionLabels).map((label, index) => {
        const input = label.querySelector('input[type="radio"], input[type="checkbox"]');
        const value = input?.value || String.fromCharCode(65 + index);
        // Get all spans and take the last one (which is the optionText span, after optionLetter and input)
        const spans = label.querySelectorAll('span');
        const textSpan = spans.length > 0 ? spans[spans.length - 1] : null;
        const text = textSpan?.textContent?.trim() || '';
        return { value, text };
      });
      
      mcqData = {
        mcqType,
        questionText,
        options,
        correctAnswers,
        questionRange,
        requiredCount: requiredCountAttr ? parseInt(requiredCountAttr, 10) : undefined
      };
      
      // Now clear to allow re-rendering
      mcqContainer.innerHTML = '';
    } else {
      // Parse MCQ data from the original structure (not yet rendered)
      mcqData = parseMCQContainer(mcqContainer);
    }
    
    if (!mcqData) return;
    
    const { mcqType, questionText, options, correctAnswers, questionRange, requiredCount } = mcqData;
    
    // Get question numbers for this MCQ
    if (!questionNumbers || questionNumbers.length === 0) {
      questionNumbers = extractQuestionNumbersFromMCQ(mcqContainer, mcqType, questionRange);
    }
    
    if (questionNumbers.length === 0) return;
    
    // Create wrapper
    const mcqWrapper = createMCQWrapper(questionText);
    
    // Create options container
    let optionsContainer;
    if (mcqType === 'single') {
      // Single answer: Radio buttons
      const qNum = questionNumbers[0];
      const userAnswer = apiResult?.response?.analytics?.[qNum]?.userAnswer || allAnswers[qNum] || '';
      const correctAnswer = apiResult?.response?.analytics?.[qNum]?.correctAnswer || (correctAnswers[0] || '');
      const isCorrect = apiResult?.response?.analytics?.[qNum]?.isCorrect;
      
      optionsContainer = createSingleAnswerOptions(options, qNum, userAnswer, correctAnswer, isCorrect, apiResult, onAnswerChange);
    } else {
      // Multiple answer: Checkboxes
      optionsContainer = createMultipleAnswerOptions(options, questionNumbers, correctAnswers, questionRange, requiredCount, allAnswers, apiResult, onAnswerChange);
    }
    
    mcqWrapper.appendChild(optionsContainer);
    mcqContainer.innerHTML = '';
    mcqContainer.appendChild(mcqWrapper);
  });
}
